import React, { useEffect, useState } from 'react';
import ButtonsIncDec from '../ButtonsIncDec';
import { TbDiscount, TbMessage2Question, TbRosetteDiscountCheckFilled, TbUser, TbUserSquareRounded } from "react-icons/tb";
import { formatCurrency } from '../../../Utils/Formatters';
import Badge from '../Badge';
import { useLocation } from 'react-router-dom';
import { CART_ITEM_STATUS_DONE, CART_ITEM_STATUS_MOUNTING, CART_ITEM_STATUS_PENDING, CART_ITEM_STATUS_RELEASED_SHIPMENT } from '../../../Utils/Constants/Cart';
import { useOrderTypeContext } from '../../../Contexts/OrderTypeContext';
import { useCartHook } from '../../../Hooks/CartHook';
import { useCartContext } from '../../../Contexts/CartContext';
import { useStoreHook } from '../../../Hooks/StoreHook';
import { useTranslation } from 'react-i18next';

interface CardProductCartProps {
  product: any;
  showButtonsIncDec?: boolean;
  cartFinished?: boolean;
  hasExtraTax?: boolean;
  showStatus?: boolean;
}

const CardProductCart = ({
  product,
  showButtonsIncDec = true,
  cartFinished = false,
  hasExtraTax = false,
  showStatus
}: CardProductCartProps) => {

  const { t } = useTranslation();
  const { isStoreCounter } = useOrderTypeContext();
  const { loadingUpdateQuantity, loadingUpdateQuantityProductId } = useCartContext();
  const { getLinkToHome } = useStoreHook();

  const location = useLocation()
  const [isCheckout] = useState(location.pathname === `${getLinkToHome()}/checkout`)
  const [isOrderPlace] = useState(location.pathname.includes('order'));

  const isCanceled = product.status === 0;
  const textCanceled = isCanceled && 'text-gray-400';
  const classCanceled = isCanceled && `line-through ${textCanceled}`;

  const getTypeByStatus = (status: number): string => {
    const statusMap: Record<number, string> = {
      [CART_ITEM_STATUS_PENDING]: 'info',
      [CART_ITEM_STATUS_MOUNTING]: 'info',
      [CART_ITEM_STATUS_RELEASED_SHIPMENT]: 'info',
      [CART_ITEM_STATUS_DONE]: 'success',
    }
    return statusMap[status] ?? ''
  }
  const getLabelByStatus = (status: number): string => {
    const statusMap: Record<number, string> = {
      [CART_ITEM_STATUS_PENDING]: 'Enviado',
      [CART_ITEM_STATUS_MOUNTING]: 'Enviado',
      [CART_ITEM_STATUS_RELEASED_SHIPMENT]: isStoreCounter ? 'Enviado' : product.status_name,
    }
    return statusMap[status] ?? product.status_name
  }

  const { changeQuantityProduct, deleteProduct } = useCartHook();

  const handleUpdate = (quantity: number) => {
    changeQuantityProduct(product.id, quantity);
  }

  const handleDelete = (itemConferenceId: number) => {
    deleteProduct(product.id);
  }

  const [isLoading, setIsLoading] = useState<boolean>(false);
  
  useEffect(() => {
      setIsLoading((!loadingUpdateQuantityProductId && loadingUpdateQuantity) || (loadingUpdateQuantityProductId === product.id && loadingUpdateQuantity));
  }, [loadingUpdateQuantityProductId, loadingUpdateQuantity, product])

  return (
    <div className={'flex flex-row gap-1.5 md:gap-2 justify-between items-center py-1.5 md:py-2 transition-all w-full'}>
      <div className={`flex justify-between items-center gap-3 w-full transition-all ease-in-out ${isLoading && 'animate-pulse'}`}>

        <div className={`self-start leading-none text-right w-8 font-bold ${classCanceled}`}>{ product.quantity }x</div>

        <div className='flex items-center w-full gap-2'>
          <div className='flex flex-col w-full self-start'>
            <span className={`font-semibold leading-4 ${classCanceled}`} dangerouslySetInnerHTML={{__html: product.name.replace(/<br\s*\/?>/gi, ' ')}} />
            {product.subitems &&
              <div className='mt-1 text-xs'>
                {product.subitems?.map((item: any, index: number) =>
                    <div className={`flex gap-1 leading-none ${isCanceled ? textCanceled : 'text-gray-500'}`} key={index}>
                      <span className='w-full leading-4'>
                        <span className="mx-0.5"><span>{item.quantity}x</span> {item.name}</span>
                        <span className='inline-flex'>{ item.total_with_discount > 0 && `(${formatCurrency(item.total_with_discount)})`}</span>
                      </span>
                  </div>
                )}
              </div>
            }

            {(product.observation && !isCanceled) &&
              <div className='flex gap-0.5 text-gray-400 text-xs md:text-sm mt-1'>
                <div><TbMessage2Question /></div>
                <span className='leading-none'>{product.observation}</span>
              </div>
            }

            {isStoreCounter &&
              <span className='mt-1 grid items-center gap-0.5 text-xs text-gray-400 leading-none'>
                {product.seller
                  ? <div className='flex items-center gap-0.5'>
                      <span className="self-start"><TbUserSquareRounded /></span>
                      <span>{t("cart.made_by_seller_at", {seller: product.seller.name, date: product.created_at})}</span>
                    </div>
                  : <div className='flex items-center gap-0.5'>
                      <span className="self-start"><TbUser /></span>
                      <span>{t("cart.made_by_customer_at", {date: product.created_at})}</span>
                    </div>
                }
                {hasExtraTax && product.service_tax === false && <div className='flex items-center gap-0.5'><span className="self-start"><TbRosetteDiscountCheckFilled /></span><span>{t('cart.free_service_fee')}</span></div>}
                {hasExtraTax && product.manual_discount && <div className='flex items-center gap-0.5'><span className="self-start"><TbDiscount /></span><span>{t('cart.manual_discount')}</span></div>}
              </span>
            }

            {(showStatus && product.status_name && !isCanceled && !isCheckout && !isOrderPlace) &&
              <span className='-mt-0.5'>
                  <Badge type={getTypeByStatus(product.status)} outline>{ getLabelByStatus(product.status) }</Badge>
              </span>
            }

            {showButtonsIncDec &&
              <div className={`flex flex-col mt-1 leading-none text-nowrap text-sm ${isCanceled ? classCanceled : 'text-gray-500'}`}>
                {product.total_discount > 0 && <span className='line-through text-gray-400 text-xs'>{formatCurrency(product.total * product.quantity)}</span>}
                <span className={`leading-none ${product.total_discount > 0 ? 'text-xs font-semibold' : ''}`}>{formatCurrency(product.total_with_discount * product.quantity)}</span>
              </div>
            }

          </div>

          {!showButtonsIncDec &&
            <div className={`flex-col text-right leading-none flex text-nowrap text-sm ${isCanceled ? classCanceled : 'text-gray-500'}`}>
              {product.total_discount > 0 && <span className='line-through text-gray-400 text-xs'>{formatCurrency(product.total * product.quantity)}</span>}
              <span className={`leading-none ${product.total_discount > 0 ? 'text-xs font-semibold' : ''}`}>{formatCurrency(product.total_with_discount * product.quantity)}</span>
            </div>
          }

        </div>
      </div>

      {showButtonsIncDec &&
          <div className='text-sm h-8 w-22 px-0'>
            <ButtonsIncDec
              onDec={handleUpdate}
              onInc={handleUpdate}
              onDelete={handleDelete}
              showDeleteOnZero
              quantityMin={0}
              initialQuantity={product.quantity}
              product={product}
              className='h-full'
              delay={500}
              loading={isLoading}
              disabled={isLoading}
            />
          </div>
        }

  </div>
  )
}

export default CardProductCart;